import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { PI } from '../../constants-types';
import {
    AccessibleFolders,
    BulkChangeFolder,
    BulkChangeFolderResponse,
    BulkFolderResponse,
    EditFolderUserAccessRequest,
    EditFolderUserAccessResponse,
    FolderCreate,
    FolderDetailWithAccess,
    FolderEntityEmployee,
    FolderEntityJob,
    FolderPatchUpdate,
    FolderResponse
} from '../../core-data-model';
import { UserProfileSettingsService } from '../../system-services';

/**
 * v5 CRUD api endpoints for Folders (categories)
 * This talks with the v5 API which is specifically
 * designed for categories features.
 *
 * @replaces v3 folder api {@link FoldersApi}
 */
@Injectable({
    providedIn: 'root'
})
export class FoldersApiService {
    constructor(
        private http: HttpClient,
        private userProfile: UserProfileSettingsService
    ) {}

    /**
     * Returns the base api for the current environment.
     * Uses AAC. (e.g. http://develop.api.predicitveindex.com/pi)
     *
     * This getter cleans up a trailing slash if it exists.
     */
    private get apiBaseUrl() {
        return (PI?.appSettings?.foldersApiUrl ?? '').replace(/\/$/, '');
    }

    /**
     * Retrieves the details for a single folder and its direct children.
     * Does not inc lude the user access or any sub-children.
     * If the folderId param is undefined, defaults to the company's root folder.
     *
     * @param folderId the id of the folder for which the details will be retrieved
     */
    getFolderAndChildren(folderId?: string): Observable<FolderResponse> {
        return this.http.get<FolderResponse>(
            this.apiEndpoint(`folders/${folderId || 'root'}/children`)
        );
    }

    /**
     * Retrieves accessible folders in a tree-like structure beginning with
     * the given  folder, or the root folder if the folderId param is undefined
     *
     * @param folderId the id of the folder for which the details will be retrieved
     */
    getFolderTree(folderId?: string): Observable<FolderResponse> {
        const route = folderId ? `folders/${folderId}/tree` : 'folders/tree';
        return this.http.get<FolderResponse>(this.apiEndpoint(route));
    }

    /**
     * Retrieves accessible folders in a tree-like structure beginning with root.
     * Retrieves accessibility for each folder for the userId passed in.
     * If no userId passed in, it will grab it for current logged in user.
     *
     * @param userId the id of the user to fetch accessibility of each folder retrieved
     */
    getFolderTreeForUser(userId?: string): Observable<FolderResponse> {
        const route = userId ? `folders/tree/user/${userId}` : `folders/tree`;
        return this.http.get<FolderResponse>(this.apiEndpoint(route));
    }

    /**
     *
     * @param folderCreate FolderCreate info for the folder to create.
     * @returns folder create success
     */
    createFolder(folderCreate: FolderCreate): Observable<FolderResponse> {
        return this.http.post<FolderResponse>(
            this.apiEndpoint('folder'),
            folderCreate
        );
    }

    /**
     * Creates multiple folders with one request
     *
     * @param bulkCreateFolder
     * @returns bulk folder create success
     */
    bulkCreateFolder(
        bulkCreateFolder: FolderCreate[]
    ): Observable<BulkFolderResponse> {
        return this.http.post<BulkFolderResponse>(
            this.apiEndpoint('folders/bulk'),
            {
                companyGuid: this.userProfile.currentCompany,
                creates: bulkCreateFolder
            }
        );
    }

    /**
     *
     * @param folderPatch FolderPatchUpdate info for the folder update.
     * @returns folder update success
     */
    updateFolder(folderPatch: FolderPatchUpdate): Observable<FolderResponse> {
        return this.http.patch<FolderResponse>(
            this.apiEndpoint('folder'),
            folderPatch
        );
    }

    /**
     *
     * @param folderGuid Guid of the folder to delete.
     * @returns true if deletion succeeded.
     */
    deleteFolder(folderGuid: string): Observable<boolean> {
        return this.http
            .delete(this.apiEndpoint('folder'), {
                body: { folderGuid: folderGuid }
            })
            .pipe(
                map(() => true),
                catchError((err) =>
                    err.status === HttpStatusCode.NoContent
                        ? of(true)
                        : of(false)
                )
            );
    }

    getAccessibleFolders() {
        return this.http.get<AccessibleFolders>(
            this.apiEndpoint('folders/accessible')
        );
    }

    /**
     * Update user access
     *
     * Updates an array of folders to grant or revoke access to a user.
     *
     * @param requestBody
     */
    updateUserAccess(
        requestBody: EditFolderUserAccessRequest
    ): Observable<EditFolderUserAccessResponse> {
        return this.http.post<EditFolderUserAccessResponse>(
            this.apiEndpoint('folders/useraccess'),
            requestBody
        );
    }

    /**
     * Get Folder Details With Access
     *
     * Gets details for a specified folder ID with user access information.
     *
     * @param folderId - ID of folder
     */
    getFolderDetailsWithAccess(
        folderId: string
    ): Observable<FolderDetailWithAccess> {
        return this.http.get<FolderDetailWithAccess>(
            this.apiEndpoint(`folders/${folderId}`)
        );
    }

    /**
     * Gets the jobs (positions) for a folder.
     *
     * @param folderId
     */
    getFolderJobs(folderId: string) {
        return this.http.get<FolderEntityJob[]>(
            this.apiEndpoint(`folders/${folderId}/jobs`)
        );
    }

    /**
     * Gets the employees (survey users) for a folder.
     *
     * @param folderId
     */
    getFolderEmployees(folderId: string) {
        return this.http.get<FolderEntityEmployee[]>(
            this.apiEndpoint(`folders/${folderId}/employees`)
        );
    }

    /**
     * Bulk move Jobs (positions) and Employees (survey users).
     * Attached surveyUsers will be moved to the target folder with the job.
     *
     * @param bulkChangeFolder
     * @returns BulkChangeFolderResponse containing the number of requested and moved jobs
     */
    bulkChangeFolder(
        bulkChangeFolder: BulkChangeFolder
    ): Observable<BulkChangeFolderResponse> {
        return this.http.post<BulkChangeFolderResponse>(
            this.apiEndpoint('folders/entities/changefolder/bulk'),
            bulkChangeFolder
        );
    }

    /**
     *
     * @param endpoint The endpoint to call usually controller name and forward
     * @param [companyGuid] Optionally add the company route.
     * @returns The fully formed url for the api resource.
     */
    private apiEndpoint(endpoint: string, includeCompanyRoute = true): string {
        // remove 0-2 slahes at beginning or end of string.
        endpoint = endpoint.replace(/(^\/{0,2})|(\/{0,2}$)/g, '');
        const parts = [this.apiBaseUrl, 'api/v5'];

        // if includeCompanyRoute requested add the company route url params
        if (includeCompanyRoute) {
            parts.push(...['companies', this.userProfile.currentCompany]);
        }
        parts.push(endpoint);

        return parts.join('/');
    }
}
